import { useMutation, useQuery } from "@tanstack/react-query";
import { deletePunchList, fetchPunchLists } from "../services/punchListsAPI";
import { useAuth } from "../hooks/useAuth";
import { useRef, useState } from "react";
import {
  Button,
  Dropdown,
  Input,
  Menu,
  Popconfirm,
  Space,
  Table,
  notification,
} from "antd";
import { EditOutlined, SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import CreatePunchListModal from "../components/CreatePunchListModal";
import UpdatePunchListModal from "../components/UpdatePunchListModal";
import queryClient from "../queryClient";
import { useNavigate } from "react-router-dom";
import { getPreferredPageSize, savePreferredPageSize } from "../utils/paginationUtils";

const PunchLists = () => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const userRole = selectedProject?.role;
  const hasFullPunchListAccess =
    userRole === "owner" ||
    userRole === "project_manager" ||
    userRole === "field";
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [selectedPunchList, setSelectedPunchList] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: getPreferredPageSize('punch_lists'),
    total: 0,
  });

  const {
    data: punchListData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [
      "punch-lists",
      projectId,
      pagination.current,
      pagination.pageSize,
    ],
    queryFn: () =>
      fetchPunchLists(projectId, pagination.current, pagination.pageSize),
    enabled: !!projectId,
    keepPreviousData: true,
  });

  const deleteMutation = useMutation({
    mutationFn: deletePunchList,
    onSuccess: () => {
      queryClient.invalidateQueries(["punch-lists", projectId]);
      notification.success({ message: "Punch list deleted successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to delete punch list",
        description: error.message,
      });
    },
  });

  if (isError) {
    notification.error({
      message: "Failed to load Punch Lists",
      description: error.message,
    });
  }

  const handleUpdateClick = (punchList) => {
    setSelectedPunchList(punchList);
    setIsUpdateModalVisible(true);
  };

  const handleDeleteClick = (punchListId) => {
    deleteMutation.mutate(punchListId);
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    confirm();
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, confirm)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleCellClick = (record) => {
    navigate(`/punch-lists/${record.id}`);
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    savePreferredPageSize(newPagination.pageSize, 'punch_lists');
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: "100%",
      onCell: (record, rowIndex) => ({
        onClick: () => handleCellClick(record, rowIndex, "name"),
      }),
      ...getColumnSearchProps("title"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) =>
        hasFullPunchListAccess && (
          <Dropdown
            overlay={
              <Menu>
                {hasFullPunchListAccess && (
                  <>
                    <Menu.Item
                      key="1"
                      onClick={() => handleUpdateClick(record)}
                    >
                      <Button className="w-full">Update</Button>
                    </Menu.Item>
                    <Menu.Item key="2">
                      <Popconfirm
                        title="Are you sure you want to delete this Punch List?"
                        onConfirm={() => handleDeleteClick(record.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button className="w-full" danger>
                          Delete
                        </Button>
                      </Popconfirm>
                    </Menu.Item>
                  </>
                )}
              </Menu>
            }
            trigger={["click"]}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Button icon={<EditOutlined />} size="medium"></Button>
          </Dropdown>
        ),
    },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Punch Lists</h1>
      {hasFullPunchListAccess && (
        <Button
          type="primary"
          onClick={() => setIsCreateModalVisible(true)}
          className="mb-4"
        >
          Create
        </Button>
      )}

      <Table
        columns={columns}
        dataSource={punchListData?.punchLists}
        rowKey="id"
        loading={isLoading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: punchListData?.total,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        onChange={handleTableChange}
        rowClassName={() => "cursor-pointer"}
      />

      <CreatePunchListModal
        visible={isCreateModalVisible}
        onClose={() => setIsCreateModalVisible(false)}
      />

      {selectedPunchList && (
        <UpdatePunchListModal
          visible={isUpdateModalVisible}
          onClose={() => setIsUpdateModalVisible(false)}
          punchList={selectedPunchList}
        />
      )}
    </div>
  );
};

export default PunchLists;
